import { useRef, useState } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { Button } from './components/ui';
import AuthLayout, { AuthAlert, AuthField, AuthHead, EMAIL_RE, homeFor } from './components/auth/AuthLayout';
import { loginProblem } from './loginProblem';
import './LoginPage.css';

function LoginPage() {
    const { login, isAuthenticated, isInitialized, user } = useAuth();
    const navigate = useNavigate();
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showPw, setShowPw] = useState(false);
    const [tried, setTried] = useState(false);
    const [busy, setBusy] = useState(false);
    const [problem, setProblem] = useState(null);
    const emailRef = useRef(null);
    const pwRef = useRef(null);

    if (!isInitialized) return null;
    if (isAuthenticated && user) return <Navigate to={homeFor(user.role)} replace />;

    const emailErr = !email.trim() ? 'Enter your email.' : !EMAIL_RE.test(email.trim()) ? 'That doesn’t look like an email address.' : '';
    const pwErr = !password ? 'Enter your password.' : '';

    async function handleLogin(e) {
        e.preventDefault();
        if (busy) return;
        if (emailErr || pwErr) {
            setTried(true);
            (emailErr ? emailRef : pwRef).current?.focus();
            return;
        }
        setProblem(null);
        setBusy(true);
        try {
            const data = await login(email.trim(), password);
            navigate(homeFor(data.user.role), { replace: true });
        } catch (err) {
            setProblem(loginProblem(err));
            setBusy(false);
        }
    }

    const edit = (set) => (e) => {
        set(e.target.value);
        if (problem) setProblem(null);
    };

    return (
        <AuthLayout>
            <AuthHead kicker="Welcome back" title="Log in" />
            {problem ? (
                <AuthAlert
                    title={problem.title}
                    action={problem.verify ? (
                        <Link className="au-link" to="/verify-email" state={{ email: email.trim(), from: 'login' }}>{problem.verify}</Link>
                    ) : problem.admin ? (
                        <Link className="au-link" to="/admin">{problem.admin}</Link>
                    ) : null}
                >
                    {problem.text}
                </AuthAlert>
            ) : null}
            <form className="au-form" onSubmit={handleLogin} noValidate>
                <AuthField
                    label="Email"
                    type="email"
                    name="email"
                    autoComplete="email"
                    inputMode="email"
                    value={email}
                    onChange={edit(setEmail)}
                    error={tried ? emailErr : ''}
                    inputRef={emailRef}
                />
                <AuthField
                    label="Password"
                    type={showPw ? 'text' : 'password'}
                    name="password"
                    autoComplete="current-password"
                    value={password}
                    onChange={edit(setPassword)}
                    error={tried ? pwErr : ''}
                    inputRef={pwRef}
                    trailing={(
                        <button
                            type="button"
                            className="au-link au-link--sm"
                            aria-label={showPw ? 'Hide password' : 'Show password'}
                            aria-pressed={showPw}
                            onClick={() => setShowPw((v) => !v)}
                        >
                            {showPw ? 'Hide' : 'Show'}
                        </button>
                    )}
                />
                <Button type="submit" variant="primary" size="lg" block aria-disabled={busy || undefined} className="lp-submit">
                    {busy ? 'Logging in…' : 'Log in'}
                </Button>
            </form>
            <div className="au-switch">
                <span className="au-switch__text">New to enAble?</span>
                <Link className="au-link" to="/sign-up">Create an account</Link>
            </div>
        </AuthLayout>
    );
}

export default LoginPage;
